<script setup lang="ts">
// App-level error boundary. Catches uncaught render / lifecycle errors in
// child components and shows a recoverable fallback UI instead of a blank
// app. Logs to the browser console + (when wired) to /errors so we can
// triage in production.
//
// Wrap once at the root; views inside the boundary continue to render
// normally. The boundary resets when the route changes so the user can
// navigate out of the crashed view without a full page reload.

import { onErrorCaptured, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AlertTriangle, RefreshCw, Home } from 'lucide-vue-next'
import Button from '@/components/ui/Button.vue'

const error = ref<Error | null>(null)
const errorInfo = ref<string>('')
const route = useRoute()
const router = useRouter()

onErrorCaptured((err, _instance, info) => {
  error.value = err as Error
  errorInfo.value = info
  // Browser console shows full stack — also POST to /errors when available.
  // We swallow further propagation so the app keeps running; the route
  // change watcher below clears the state automatically.
  console.error('[ErrorBoundary]', info, err)
  try {
    void fetch('/api/v1/_client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        message: String((err as Error)?.message ?? err),
        stack: String((err as Error)?.stack ?? ''),
        info,
        path: window.location.pathname,
        ua: navigator.userAgent,
      }),
    }).catch(() => { /* best-effort */ })
  } catch { /* offline */ }
  return false
})

watch(() => route.fullPath, () => {
  error.value = null
  errorInfo.value = ''
})

function retry() {
  // Simplest reset: re-navigate to the current path so router pulls a
  // fresh component instance.
  const target = route.fullPath
  void router.replace('/').then(() => router.push(target))
}

function goHome() {
  error.value = null
  void router.push('/')
}
</script>

<template>
  <slot v-if="!error" />
  <div
    v-else
    class="min-h-screen flex items-center justify-center p-6 bg-background"
    role="alert"
  >
    <div class="max-w-md w-full bg-card text-card-foreground rounded-card p-8 space-y-5">
      <div class="flex items-start gap-4">
        <div class="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
          <AlertTriangle class="h-6 w-6" />
        </div>
        <div class="space-y-1.5 min-w-0">
          <h1 class="text-lg font-semibold leading-tight">
            {{ $t('errors.boundary.title') }}
          </h1>
          <p class="text-sm text-muted-foreground leading-relaxed">
            {{ $t('errors.boundary.subtitle') }}
          </p>
        </div>
      </div>
      <details class="text-xs text-muted-foreground rounded-lg p-3 bg-muted">
        <summary class="cursor-pointer font-medium text-foreground">
          {{ $t('errors.boundary.details') }}
        </summary>
        <p class="mt-2 font-mono break-all">
          {{ error.message }}
        </p>
        <p
          v-if="errorInfo"
          class="mt-1 text-[10px] uppercase tracking-wider opacity-70"
        >
          {{ errorInfo }}
        </p>
      </details>
      <div class="flex flex-wrap gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          @click="goHome"
        >
          <Home class="h-4 w-4" /> {{ $t('errors.boundary.go_home') }}
        </Button>
        <Button
          size="sm"
          @click="retry"
        >
          <RefreshCw class="h-4 w-4" /> {{ $t('errors.boundary.retry') }}
        </Button>
      </div>
    </div>
  </div>
</template>
